import React, { useEffect, useRef } from 'react';
import * as PIXI from 'pixi.js';
import { useTheme } from './theme/ThemeProvider';
import { createTableBase } from './visual/TableBase';
import { createFeltSurface } from './visual/FeltSurface';
import { createPositionMarker } from './visual/PositionMarkers';
import { createChipStack } from './visual/ChipStack';
import { createCardSprite } from './visual/CardSprite';
import { getCardApiImageUrl, getCardApiBackUrl } from './visual/cardApi';
import { ParticleSystem } from './effects/particleSystem';
import { animateWinningHand } from './animation/cardAnimations';
import { animatePotCollection } from './animation/chipAnimations';
import { useTableStore } from './state/tableStore';
import { animatePlayerIdle, animateActionRequired, animateTimerCritical, animateWinningPlayer, animateEliminatedPlayer } from './animation/playerAnimations';
import { FederatedPointerEvent, Container, Graphics, Sprite, Text, TextStyle } from 'pixi.js';

export const PokerTableStage: React.FC = () => {
  const pixiRef = useRef<HTMLDivElement>(null);
  const appRef = useRef<PIXI.Application | null>(null);
  const { theme } = useTheme();
  const { players, seats, currentPlayerSeat, phase, animationState, handResult, communityCards } = useTableStore();
  const rebuildStage = React.useCallback(
    (app: PIXI.Application) => {
      const existing = app.stage.removeChildren();
      existing.forEach((child) => {
        if (typeof (child as any).destroy === 'function') {
          (child as any).destroy({ children: true });
        }
      });
      const w = window.innerWidth;
      const h = window.innerHeight;
      const tableBase = createTableBase(w, h, theme);
      app.stage.addChild(tableBase);
      const felt = createFeltSurface(w, h, theme);
      app.stage.addChild(felt);
      const seatCount = seats.length > 0 ? seats.length : 6;
      const cx = w / 2;
      const cy = h / 2;
      const r = Math.min(w, h) * 0.32;
      const winnerSeats = new Set(
        handResult?.pots?.flatMap((pot) => pot.winners.map((winner) => winner.seat)) ?? []
      );
      for (let i = 0; i < seatCount; i++) {
        const seat = seats.find((s) => s.seat === i);
        const angle = ((Math.PI * 2) / seatCount) * i - Math.PI / 2;
        const x = cx + r * Math.cos(angle);
        const y = cy + r * Math.sin(angle);
        const isWinner = winnerSeats.has(i);
        const isTurn = i === currentPlayerSeat && phase !== 'idle';
        const markerColor = isWinner ? '#facc15' : isTurn ? '#38bdf8' : theme.accent;
        const marker = createPositionMarker(x, y, 36, markerColor, true);
  (marker as any).label = `marker-seat${i}`;
        app.stage.addChild(marker);
        if (seat?.userId) {
          const chips = Number(seat.chips ?? 0);
          const stackCount = Math.max(1, Math.min(10, Math.round(chips / 1000)));
          const chipStack = createChipStack(x, y + 48, [stackCount], theme.chipColors, `seat${i}`);
          app.stage.addChild(chipStack);
          const displayName = seat.displayName || seat.userId;
          const name = displayName.length > 12 ? `${displayName.slice(0, 10)}…` : displayName;
          const initials = displayName
            .split(' ')
            .filter(Boolean)
            .slice(0, 2)
            .map((part: string) => part[0]?.toUpperCase())
            .join('') || displayName.slice(0, 2).toUpperCase();
          const badge = new PIXI.Graphics();
          badge.circle(x, y - 52, 18).fill({ color: 0x0f172a, alpha: 0.9 });
          badge.circle(x, y - 52, 18).stroke({ width: 2, color: isWinner ? 0xfacc15 : 0x38bdf8, alpha: 0.9 });
          app.stage.addChild(badge);
          if (seat.avatarUrl) {
            const avatarTexture = PIXI.Texture.from(seat.avatarUrl);
            const avatar = new PIXI.Sprite(avatarTexture);
            avatar.anchor.set(0.5);
            avatar.x = x;
            avatar.y = y - 52;
            avatar.width = 28;
            avatar.height = 28;
            const mask = new PIXI.Graphics();
            mask.circle(x, y - 52, 14).fill({ color: 0xffffff, alpha: 1 });
            avatar.mask = mask;
            app.stage.addChild(mask);
            app.stage.addChild(avatar);
          } else {
            const initialLabel = new Text(initials, new TextStyle({ fill: '#e2e8f0', fontSize: 12, fontWeight: '700' }));
            initialLabel.anchor.set(0.5);
            initialLabel.x = x;
            initialLabel.y = y - 52;
            app.stage.addChild(initialLabel);
          }
          const labelStyle = new TextStyle({
            fill: isWinner ? '#facc15' : '#e2e8f0',
            fontSize: 14,
            fontWeight: '600',
          });
          const nameLabel = new Text(name, labelStyle);
          nameLabel.anchor.set(0.5);
          nameLabel.x = x;
          nameLabel.y = y + 70;
          app.stage.addChild(nameLabel);
          const chipLabel = new Text(`$${chips.toLocaleString()}`, new TextStyle({ fill: '#94a3b8', fontSize: 12 }));
          chipLabel.anchor.set(0.5);
          chipLabel.x = x;
          chipLabel.y = y + 88;
          app.stage.addChild(chipLabel);
        }
      }
      const cardBack = PIXI.Texture.from(getCardApiBackUrl());
      const commY = cy;
      const commStartX = cx - 120;
      for (let i = 0; i < 5; i++) {
        const cardCode = communityCards?.[i] ?? null;
        const faceUp = Boolean(cardCode);
        const cardFace = cardCode ? PIXI.Texture.from(getCardApiImageUrl(cardCode)) : cardBack;
        const card = createCardSprite(
          commStartX + i * 60,
          commY,
          cardFace,
          cardBack,
          faceUp,
          `comm${i}`
        );
        app.stage.addChild(card);
      }
    },
      [players, seats, currentPlayerSeat, phase, handResult, theme, communityCards]
    );
  useEffect(() => {
    if (!pixiRef.current || appRef.current) return;
    let isDestroyed = false;
    const app = new PIXI.Application();
    (async () => {
      await app.init({
        resizeTo: window,
        backgroundAlpha: 0,
        antialias: true,
        resolution: window.devicePixelRatio || 1,
        autoDensity: true,
      });
      if (isDestroyed) {
        app.destroy(true, { children: true });
        return;
      }
      appRef.current = app;
      app.stage.sortableChildren = true;
      if (pixiRef.current && app.canvas) {
        pixiRef.current.appendChild(app.canvas);
      }
      if (app.canvas) {
        app.canvas.addEventListener('webglcontextlost', (event) => {
          event.preventDefault();
        });
        app.canvas.addEventListener('webglcontextrestored', () => {
          rebuildStage(app);
        });
      }
      rebuildStage(app);
    })();
    // TODO: Render player hole cards if available in state
    // Particle system for effects (optional, can be triggered by state changes)
    // const particleSystem = new ParticleSystem(app.stage);
    return () => {
      if (isDestroyed) return;
      isDestroyed = true;
      app.stage.removeAllListeners();
      app.ticker?.stop();
      if (app.renderer) {
        app.destroy(true, { children: true });
      }
      appRef.current = null;
    };
  }, [rebuildStage]);
  useEffect(() => {
    const app = appRef.current;
    if (!app || !app.renderer) return;
    rebuildStage(app);
  }, [rebuildStage]);
  useEffect(() => {
    const app = appRef.current;
    if (!app || !app.renderer) return;
    // State-driven player marker animation
    app.stage.children.forEach((child: Container) => {
      const label = (child as any).label ?? (child as any).name;
      if (label && label.startsWith('marker-seat')) {
        const seatIdx = parseInt(label.replace('marker-seat', ''));
        if (players[seatIdx]?.isEliminated) animateEliminatedPlayer(child as Graphics);
        else if (seatIdx === currentPlayerSeat && phase !== 'idle') animateActionRequired(child as Graphics);
        else animatePlayerIdle(child as Graphics);
      }
    });
    // Micro-interaction: hover effect for cards/chips
    app.stage.interactive = true;
    const handleMove = (e: FederatedPointerEvent) => {
      app.stage.children.forEach((child: Container) => {
        const label = (child as any).label ?? (child as any).name;
        if (label && label.startsWith('comm')) {
          const card = child as Container;
          const bounds = (card.getBounds() as any);
          if (
            e.global.x >= bounds.x &&
            e.global.x <= bounds.x + bounds.width &&
            e.global.y >= bounds.y &&
            e.global.y <= bounds.y + bounds.height
          ) {
            card.scale.set(1.08);
            card.zIndex = 100;
          } else {
            card.scale.set(1);
            card.zIndex = 1;
          }
        }
        if (label && label.startsWith('chip-stack')) {
          child.children.forEach((chip) => {
            if (typeof (chip as any).getBounds === 'function') {
              const bounds = (chip as any).getBounds() as PIXI.Rectangle;
              if (
                e.global.x >= bounds.x &&
                e.global.x <= bounds.x + bounds.width &&
                e.global.y >= bounds.y &&
                e.global.y <= bounds.y + bounds.height
              ) {
                (chip as any).scale?.set?.(1.12);
              } else {
                (chip as any).scale?.set?.(1);
              }
            }
          });
        }
      });
    };
    app.stage.on('mousemove', handleMove);
    return () => {
      app.stage.off('mousemove', handleMove);
    };
  }, [players, currentPlayerSeat, phase, animationState]);
  return (
    <div
      ref={pixiRef}
      style={{
        width: '100vw',
        height: '100vh',
        position: 'absolute',
        top: 0,
        left: 0,
        zIndex: 1,
      }}
    />
  );
};
